// Endpoint-targeted telemetry redaction for the Hybrid Courses feature.
// Instructor names and instructor-search text are sensitive. They must never
// reach console.*, Sentry exception payloads, breadcrumbs, or manually
// attached Sentry context. Diagnostics may contain only the endpoint
// template, HTTP status, error code, tenant ID, and group ID.
//
// PERFCS-2281 recognizes only the instructor lookup and individual
// submission endpoints. PERFCS-2289 later extends the same helpers for the
// bulk edit endpoint without replacing these contracts.

const REDACTED = '[REDACTED]';
const SENSITIVE_VUE_COMPONENTS = new Set([
  '<BulkEditConfirmationModal>',
  '<BulkEditSelectionModal>',
  '<InPersonTrainingFields>',
  '<InstructorNameInput>',
]);

// Recognized endpoint URL templates:
// - courseAssignment/inPersonTraining/instructors
// - courseAssignment/{courseAssignmentId}/inPersonTraining
// - courseAssignment/bulkEdit (PERFCS-2289)
const HYBRID_COURSES_ENDPOINT_PATTERNS = [
  /courseAssignment\/inPersonTraining\/instructors/,
  /courseAssignment\/[^/?#]+\/inPersonTraining/,
  /courseAssignment\/bulkEdit/,
];

const isMatchingEndpoint = (url) => {
  if (typeof url !== 'string' || !url) return false;
  const path = url.split('?')[0];
  return HYBRID_COURSES_ENDPOINT_PATTERNS.some((pattern) => pattern.test(path));
};

// Redact the `search` query value in a matching URL while preserving the
// path and every other query key.
const redactSearchQueryValue = (url) => {
  if (typeof url !== 'string' || !url) return url;
  const queryIndex = url.indexOf('?');
  if (queryIndex === -1) return url;
  const path = url.slice(0, queryIndex);
  const redactedQuery = url
    .slice(queryIndex + 1)
    .split('&')
    .map((pair) => {
      const separatorIndex = pair.indexOf('=');
      const key = separatorIndex === -1 ? pair : pair.slice(0, separatorIndex);
      return key === 'search' ? `${key}=${REDACTED}` : pair;
    })
    .join('&');
  return `${path}?${redactedQuery}`;
};

const getEndpointTemplate = (url) => {
  if (/courseAssignment\/inPersonTraining\/instructors/.test(url)) {
    return 'courseAssignment/inPersonTraining/instructors';
  }
  if (/courseAssignment\/bulkEdit/.test(url)) {
    return 'courseAssignment/bulkEdit';
  }
  return 'courseAssignment/{courseAssignmentId}/inPersonTraining';
};

// Return a new allowlisted Error for matching requests. Axios errors retain
// request headers through config/toJSON, so cloning one can expose bearer and
// fingerprint values even after its body and query values are redacted.
export const sanitizeHybridCoursesAxiosErrorForTelemetry = (error) => {
  if (!error || typeof error !== 'object') return error;
  const configUrl = error.config && error.config.url;
  if (!isMatchingEndpoint(configUrl)) return error;

  const headers = error.config.headers || {};
  return Object.assign(new Error('Hybrid Courses request failed'), {
    name: 'HybridCoursesRequestError',
    endpoint: getEndpointTemplate(configUrl),
    status: error.response && error.response.status,
    code: error.code,
    tenantId: headers.TenantId,
    groupId: headers['Group-Id'],
  });
};

// Redact only matching endpoint URLs on breadcrumbs; other breadcrumbs pass
// through unchanged.
export const sanitizeHybridCoursesBreadcrumb = (breadcrumb) => {
  if (!breadcrumb || typeof breadcrumb !== 'object') return breadcrumb;
  const url = breadcrumb.data && breadcrumb.data.url;
  if (!isMatchingEndpoint(url)) return breadcrumb;

  const sanitizedClone = { ...breadcrumb };
  if (breadcrumb.data && typeof breadcrumb.data === 'object') {
    sanitizedClone.data = { ...breadcrumb.data };
    if (typeof sanitizedClone.data.url === 'string') {
      sanitizedClone.data.url = redactSearchQueryValue(sanitizedClone.data.url);
    }
  }
  return sanitizedClone;
};

// Vue error context includes component props by default. Redact props only
// for components that receive instructor names; every other event is returned
// unchanged.
export const sanitizeHybridCoursesSentryEvent = (event) => {
  const vueContext = event?.contexts?.vue;
  const isInstructorMultiselect =
    vueContext?.componentName === '<VueMultiselect>' &&
    vueContext.trace?.includes('<InstructorNameInput>');
  if (
    !vueContext?.propsData ||
    (!SENSITIVE_VUE_COMPONENTS.has(vueContext.componentName) &&
      !isInstructorMultiselect)
  ) {
    return event;
  }

  return {
    ...event,
    contexts: {
      ...event.contexts,
      vue: { ...vueContext, propsData: { redacted: REDACTED } },
    },
  };
};

// Suppress BrowserTracing spans only for matching Hybrid Courses requests.
// The requests themselves still execute normally; only their tracing spans
// are skipped. All other requests keep their spans.
export const shouldCreateHybridCoursesSpanForRequest = (url) =>
  !isMatchingEndpoint(url);
